import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Target,
  TrendingUp,
  AlertTriangle,
  Plus,
  Trash2,
  Edit2,
  DollarSign,
  Calendar,
  Shield
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody } from '../ui/Card';
import {
  ValueNodeCreate,
  ValueNodeResponse,
  OpportunityCreate,
  OpportunityResponse,
  ChallengeCreate,
  ChallengeResponse,
} from '../../types/threePhase';
import { standaloneCompaniesAPI } from '../../lib/api';

// Validation schemas
const valueNodeSchema = z.object({
  category: z.string().min(2, 'Category must be at least 2 characters'),
  currentValue: z.number().min(0, 'Current value must be non-negative'),
  targetValue: z.number().min(0, 'Target value must be non-negative'),
  unit: z.string().min(1, 'Unit is required'),
  timeframe: z.string().optional(),
});

const opportunitySchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  estimatedValueUSD: z.number().positive('Estimated value must be positive'),
  timeframe: z.string().min(2, 'Timeframe is required'),
  probability: z.number().min(0).max(100).optional(),
  strategicAlignment: z.string().optional(),
});

const challengeSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  impact: z.enum(['Low', 'Medium', 'High', 'Critical'], {
    errorMap: () => ({ message: 'Select a valid impact level' }),
  }),
  likelihood: z.enum(['Low', 'Medium', 'High'], {
    errorMap: () => ({ message: 'Select a valid likelihood level' }),
  }),
  mitigationStrategy: z.string().optional(),
  estimatedCostUSD: z.number().positive().optional(),
});

interface ValueTreeBuilderProps {
  caseId: string;
}

type TabType = 'valueNodes' | 'opportunities' | 'challenges';

const ValueTreeBuilder: React.FC<ValueTreeBuilderProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('valueNodes');

  // Editing state
  const [editingValueNode, setEditingValueNode] = useState<ValueNodeResponse | null>(null);
  const [editingOpportunity, setEditingOpportunity] = useState<OpportunityResponse | null>(null);
  const [editingChallenge, setEditingChallenge] = useState<ChallengeResponse | null>(null);

  // ===== VALUE NODES =====
  const {
    register: registerValueNode,
    handleSubmit: handleSubmitValueNode,
    formState: { errors: valueNodeErrors },
    reset: resetValueNode,
    setValue: setValueNodeValue,
  } = useForm<ValueNodeCreate>({
    resolver: zodResolver(valueNodeSchema),
    defaultValues: {
      category: '',
      currentValue: 0,
      targetValue: 0,
      unit: '',
      timeframe: '',
    },
  });

  const { data: valueNodes } = useQuery({
    queryKey: ['valueNodes', caseId],
    queryFn: async () => {
      const response = await standaloneCompaniesAPI.getAllValueNodes(caseId);
      return response.data as ValueNodeResponse[];
    },
    enabled: !!caseId,
  });

  const createValueNodeMutation = useMutation({
    mutationFn: (data: ValueNodeCreate) => standaloneCompaniesAPI.createValueNode(caseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valueNodes', caseId] });
      toast.success('Value node added successfully');
      resetValueNode();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add value node');
    },
  });

  const updateValueNodeMutation = useMutation({
    mutationFn: ({ valueNodeId, data }: { valueNodeId: string; data: ValueNodeCreate }) =>
      standaloneCompaniesAPI.updateValueNode(caseId, valueNodeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valueNodes', caseId] });
      toast.success('Value node updated successfully');
      resetValueNode();
      setEditingValueNode(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update value node');
    },
  });

  const deleteValueNodeMutation = useMutation({
    mutationFn: (valueNodeId: string) => standaloneCompaniesAPI.deleteValueNode(caseId, valueNodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valueNodes', caseId] });
      toast.success('Value node deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete value node');
    },
  });

  const handleValueNodeSubmit = (data: ValueNodeCreate) => {
    if (editingValueNode) {
      updateValueNodeMutation.mutate({ valueNodeId: editingValueNode.valueNodeId, data });
    } else {
      createValueNodeMutation.mutate(data);
    }
  };

  const handleEditValueNode = (node: ValueNodeResponse) => {
    setEditingValueNode(node);
    setValueNodeValue('category', node.category);
    setValueNodeValue('currentValue', node.currentValue);
    setValueNodeValue('targetValue', node.targetValue);
    setValueNodeValue('unit', node.unit);
    setValueNodeValue('timeframe', node.timeframe);
  };

  const handleDeleteValueNode = (valueNodeId: string) => {
    if (confirm('Are you sure you want to delete this value node?')) {
      deleteValueNodeMutation.mutate(valueNodeId);
    }
  };

  // ===== OPPORTUNITIES =====
  const {
    register: registerOpportunity,
    handleSubmit: handleSubmitOpportunity,
    formState: { errors: opportunityErrors },
    reset: resetOpportunity,
    setValue: setOpportunityValue,
  } = useForm<OpportunityCreate>({
    resolver: zodResolver(opportunitySchema),
    defaultValues: {
      title: '',
      description: '',
      estimatedValueUSD: 0,
      timeframe: '',
      probability: undefined,
      strategicAlignment: '',
    },
  });

  const { data: opportunities } = useQuery({
    queryKey: ['opportunities', caseId],
    queryFn: async () => {
      const response = await standaloneCompaniesAPI.getAllOpportunities(caseId);
      return response.data as OpportunityResponse[];
    },
    enabled: !!caseId,
  });

  const createOpportunityMutation = useMutation({
    mutationFn: (data: OpportunityCreate) => standaloneCompaniesAPI.createOpportunity(caseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', caseId] });
      toast.success('Opportunity added successfully');
      resetOpportunity();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add opportunity');
    },
  });

  const updateOpportunityMutation = useMutation({
    mutationFn: ({ opportunityId, data }: { opportunityId: string; data: OpportunityCreate }) =>
      standaloneCompaniesAPI.updateOpportunity(caseId, opportunityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', caseId] });
      toast.success('Opportunity updated successfully');
      resetOpportunity();
      setEditingOpportunity(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update opportunity');
    },
  });

  const deleteOpportunityMutation = useMutation({
    mutationFn: (opportunityId: string) => standaloneCompaniesAPI.deleteOpportunity(caseId, opportunityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', caseId] });
      toast.success('Opportunity deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete opportunity');
    },
  });

  const handleOpportunitySubmit = (data: OpportunityCreate) => {
    if (editingOpportunity) {
      updateOpportunityMutation.mutate({ opportunityId: editingOpportunity.opportunityId, data });
    } else {
      createOpportunityMutation.mutate(data);
    }
  };

  const handleEditOpportunity = (opp: OpportunityResponse) => {
    setEditingOpportunity(opp);
    setOpportunityValue('title', opp.title);
    setOpportunityValue('description', opp.description);
    setOpportunityValue('estimatedValueUSD', opp.estimatedValueUSD);
    setOpportunityValue('timeframe', opp.timeframe);
    setOpportunityValue('probability', opp.probability);
    setOpportunityValue('strategicAlignment', opp.strategicAlignment);
  };

  const handleDeleteOpportunity = (opportunityId: string) => {
    if (confirm('Are you sure you want to delete this opportunity?')) {
      deleteOpportunityMutation.mutate(opportunityId);
    }
  };

  // ===== CHALLENGES =====
  const {
    register: registerChallenge,
    handleSubmit: handleSubmitChallenge,
    formState: { errors: challengeErrors },
    reset: resetChallenge,
    setValue: setChallengeValue,
  } = useForm<ChallengeCreate>({
    resolver: zodResolver(challengeSchema),
    defaultValues: {
      title: '',
      description: '',
      impact: 'Medium',
      likelihood: 'Medium',
      mitigationStrategy: '',
      estimatedCostUSD: undefined,
    },
  });

  const { data: challenges } = useQuery({
    queryKey: ['challenges', caseId],
    queryFn: async () => {
      const response = await standaloneCompaniesAPI.getAllChallenges(caseId);
      return response.data as ChallengeResponse[];
    },
    enabled: !!caseId,
  });

  const createChallengeMutation = useMutation({
    mutationFn: (data: ChallengeCreate) => standaloneCompaniesAPI.createChallenge(caseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', caseId] });
      toast.success('Challenge added successfully');
      resetChallenge();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add challenge');
    },
  });

  const updateChallengeMutation = useMutation({
    mutationFn: ({ challengeId, data }: { challengeId: string; data: ChallengeCreate }) =>
      standaloneCompaniesAPI.updateChallenge(caseId, challengeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', caseId] });
      toast.success('Challenge updated successfully');
      resetChallenge();
      setEditingChallenge(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update challenge');
    },
  });

  const deleteChallengeMutation = useMutation({
    mutationFn: (challengeId: string) => standaloneCompaniesAPI.deleteChallenge(caseId, challengeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', caseId] });
      toast.success('Challenge deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete challenge');
    },
  });

  const handleChallengeSubmit = (data: ChallengeCreate) => {
    if (editingChallenge) {
      updateChallengeMutation.mutate({ challengeId: editingChallenge.challengeId, data });
    } else {
      createChallengeMutation.mutate(data);
    }
  };

  const handleEditChallenge = (challenge: ChallengeResponse) => {
    setEditingChallenge(challenge);
    setChallengeValue('title', challenge.title);
    setChallengeValue('description', challenge.description);
    setChallengeValue('impact', challenge.impact);
    setChallengeValue('likelihood', challenge.likelihood);
    setChallengeValue('mitigationStrategy', challenge.mitigationStrategy);
    setChallengeValue('estimatedCostUSD', challenge.estimatedCostUSD);
  };

  const handleDeleteChallenge = (challengeId: string) => {
    if (confirm('Are you sure you want to delete this challenge?')) {
      deleteChallengeMutation.mutate(challengeId);
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Critical': return 'text-red-700 bg-red-50';
      case 'High': return 'text-orange-700 bg-orange-50';
      case 'Medium': return 'text-yellow-700 bg-yellow-50';
      case 'Low': return 'text-green-700 bg-green-50';
      default: return 'text-neutral-700 bg-neutral-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-neutral-200">
        <nav className="flex gap-4">
          <button
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'valueNodes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-600 hover:text-neutral-900'
            }`}
            onClick={() => setActiveTab('valueNodes')}
          >
            <Target className="h-4 w-4 inline mr-2" />
            Value Nodes
          </button>
          <button
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'opportunities'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-600 hover:text-neutral-900'
            }`}
            onClick={() => setActiveTab('opportunities')}
          >
            <TrendingUp className="h-4 w-4 inline mr-2" />
            Opportunities
          </button>
          <button
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'challenges'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-600 hover:text-neutral-900'
            }`}
            onClick={() => setActiveTab('challenges')}
          >
            <AlertTriangle className="h-4 w-4 inline mr-2" />
            Challenges
          </button>
        </nav>
      </div>

      {/* VALUE NODES TAB */}
      {activeTab === 'valueNodes' && (
        <div className="space-y-6">
          {/* Existing Value Nodes */}
          {valueNodes && valueNodes.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary-500" />
                  <h3 className="text-lg font-semibold">Value Nodes ({valueNodes.length})</h3>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {valueNodes.map((node) => (
                    <div
                      key={node.valueNodeId}
                      className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-neutral-900">{node.category}</h4>
                        <div className="flex gap-4 mt-2 text-sm text-neutral-600">
                          <span>Current: {node.currentValue} {node.unit}</span>
                          <span className="text-primary-600">→ Target: {node.targetValue} {node.unit}</span>
                          {node.timeframe && <span>Timeframe: {node.timeframe}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditValueNode(node)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteValueNode(node.valueNodeId)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Add/Edit Value Node Form */}
          <form onSubmit={handleSubmitValueNode(handleValueNodeSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary-500" />
                  <h3 className="text-lg font-semibold">
                    {editingValueNode ? 'Edit Value Node' : 'Add Value Node'}
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <Input
                  label="Category"
                  placeholder="e.g., Revenue Growth, Customer Satisfaction"
                  {...registerValueNode('category')}
                  error={valueNodeErrors.category?.message}
                  required
                />

                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Current Value"
                    type="number"
                    step="0.01"
                    placeholder="100"
                    {...registerValueNode('currentValue', { valueAsNumber: true })}
                    error={valueNodeErrors.currentValue?.message}
                    required
                  />
                  <Input
                    label="Target Value"
                    type="number"
                    step="0.01"
                    placeholder="150"
                    {...registerValueNode('targetValue', { valueAsNumber: true })}
                    error={valueNodeErrors.targetValue?.message}
                    required
                  />
                  <Input
                    label="Unit"
                    placeholder="Million USD, %, etc."
                    {...registerValueNode('unit')}
                    error={valueNodeErrors.unit?.message}
                    required
                  />
                </div>

                <Input
                  label="Timeframe (Optional)"
                  placeholder="e.g., Q4 2025, 18 months"
                  {...registerValueNode('timeframe')}
                  error={valueNodeErrors.timeframe?.message}
                />
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3">
              {editingValueNode && (
                <Button type="button" variant="outline" onClick={() => {
                  setEditingValueNode(null);
                  resetValueNode();
                }}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={createValueNodeMutation.isPending || updateValueNodeMutation.isPending}
              >
                {editingValueNode ? 'Update Value Node' : 'Add Value Node'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* OPPORTUNITIES TAB */}
      {activeTab === 'opportunities' && (
        <div className="space-y-6">
          {/* Existing Opportunities */}
          {opportunities && opportunities.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <h3 className="text-lg font-semibold">Opportunities ({opportunities.length})</h3>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {opportunities.map((opp) => (
                    <div
                      key={opp.opportunityId}
                      className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-neutral-900">{opp.title}</h4>
                        {opp.description && (
                          <p className="text-sm text-neutral-600 mt-1">{opp.description}</p>
                        )}
                        <div className="flex gap-4 mt-2 text-sm text-neutral-600">
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            ${((opp.estimatedValueUSD ?? 0) / 1000000).toFixed(1)}M
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {opp.timeframe}
                          </span>
                          {opp.probability && <span>Probability: {opp.probability}%</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditOpportunity(opp)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteOpportunity(opp.opportunityId)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Add/Edit Opportunity Form */}
          <form onSubmit={handleSubmitOpportunity(handleOpportunitySubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-green-500" />
                  <h3 className="text-lg font-semibold">
                    {editingOpportunity ? 'Edit Opportunity' : 'Add Opportunity'}
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <Input
                  label="Title"
                  placeholder="e.g., Cloud Migration Cost Savings"
                  {...registerOpportunity('title')}
                  error={opportunityErrors.title?.message}
                  required
                />

                <Textarea
                  label="Description"
                  placeholder="Describe the opportunity..."
                  rows={3}
                  {...registerOpportunity('description')}
                  error={opportunityErrors.description?.message}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Estimated Value (USD)"
                    type="number"
                    placeholder="5000000"
                    {...registerOpportunity('estimatedValueUSD', { valueAsNumber: true })}
                    error={opportunityErrors.estimatedValueUSD?.message}
                    required
                  />
                  <Input
                    label="Timeframe"
                    placeholder="e.g., Q2 2025, 6 months"
                    {...registerOpportunity('timeframe')}
                    error={opportunityErrors.timeframe?.message}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Probability (%)"
                    type="number"
                    step="1"
                    placeholder="75"
                    {...registerOpportunity('probability', { valueAsNumber: true })}
                    error={opportunityErrors.probability?.message}
                  />
                  <Input
                    label="Strategic Alignment"
                    placeholder="e.g., Digital Transformation"
                    {...registerOpportunity('strategicAlignment')}
                    error={opportunityErrors.strategicAlignment?.message}
                  />
                </div>
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3">
              {editingOpportunity && (
                <Button type="button" variant="outline" onClick={() => {
                  setEditingOpportunity(null);
                  resetOpportunity();
                }}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={createOpportunityMutation.isPending || updateOpportunityMutation.isPending}
              >
                {editingOpportunity ? 'Update Opportunity' : 'Add Opportunity'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* CHALLENGES TAB */}
      {activeTab === 'challenges' && (
        <div className="space-y-6">
          {/* Existing Challenges */}
          {challenges && challenges.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold">Challenges ({challenges.length})</h3>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {challenges.map((challenge) => (
                    <div
                      key={challenge.challengeId}
                      className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-neutral-900">{challenge.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(challenge.impact)}`}>
                            {challenge.impact}
                          </span>
                        </div>
                        {challenge.description && (
                          <p className="text-sm text-neutral-600 mt-1">{challenge.description}</p>
                        )}
                        <div className="flex gap-4 mt-2 text-sm text-neutral-600">
                          <span>Likelihood: {challenge.likelihood}</span>
                          {challenge.estimatedCostUSD && (
                            <span>${(challenge.estimatedCostUSD / 1000000).toFixed(1)}M</span>
                          )}
                        </div>
                        {challenge.mitigationStrategy && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <span className="font-medium text-blue-900">Mitigation:</span>{' '}
                            <span className="text-blue-700">{challenge.mitigationStrategy}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditChallenge(challenge)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteChallenge(challenge.challengeId)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Add/Edit Challenge Form */}
          <form onSubmit={handleSubmitChallenge(handleChallengeSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold">
                    {editingChallenge ? 'Edit Challenge' : 'Add Challenge'}
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <Input
                  label="Title"
                  placeholder="e.g., Legacy System Integration Complexity"
                  {...registerChallenge('title')}
                  error={challengeErrors.title?.message}
                  required
                />

                <Textarea
                  label="Description"
                  placeholder="Describe the challenge..."
                  rows={3}
                  {...registerChallenge('description')}
                  error={challengeErrors.description?.message}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Impact <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      {...registerChallenge('impact')}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                    {challengeErrors.impact && (
                      <p className="text-sm text-red-500 mt-1">{challengeErrors.impact.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Likelihood <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      {...registerChallenge('likelihood')}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                    {challengeErrors.likelihood && (
                      <p className="text-sm text-red-500 mt-1">{challengeErrors.likelihood.message}</p>
                    )}
                  </div>
                </div>

                <Textarea
                  label="Mitigation Strategy"
                  placeholder="Describe how to mitigate this challenge..."
                  rows={3}
                  {...registerChallenge('mitigationStrategy')}
                  error={challengeErrors.mitigationStrategy?.message}
                />

                <Input
                  label="Estimated Cost (USD)"
                  type="number"
                  placeholder="2000000"
                  {...registerChallenge('estimatedCostUSD', { valueAsNumber: true })}
                  error={challengeErrors.estimatedCostUSD?.message}
                />
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3">
              {editingChallenge && (
                <Button type="button" variant="outline" onClick={() => {
                  setEditingChallenge(null);
                  resetChallenge();
                }}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={createChallengeMutation.isPending || updateChallengeMutation.isPending}
              >
                {editingChallenge ? 'Update Challenge' : 'Add Challenge'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ValueTreeBuilder;
